import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';

import { Button, type ButtonProps } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

export interface CopyButtonProps extends Omit<ButtonProps, 'children' | 'onClick' | 'onCopy'> {
  /** Value written to the clipboard on click. */
  value: string;
  /** Accessible label and tooltip text. Defaults to "Copy". */
  label?: string;
  /** Toast message shown after a successful copy. Defaults to "Copied to clipboard". */
  successMessage?: string;
  /** Optional callback fired after the value is copied. */
  onCopied?: (value: string) => void;
  /** Icon size in pixels. Defaults to 14. */
  iconSize?: number;
}

const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      value,
      label = 'Copy',
      successMessage = 'Copied to clipboard',
      onCopied,
      iconSize = 14,
      variant = 'ghost',
      size = 'icon',
      className,
      ...props
    },
    ref,
  ) => {
    const [copied, setCopied] = React.useState(false);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), 1500);
        toast.success(successMessage);
        onCopied?.(value);
      } catch {
        toast.error('Could not copy to clipboard');
      }
    };

    return (
      <Button
        ref={ref}
        type="button"
        variant={variant}
        size={size}
        aria-label={label}
        title={label}
        onClick={handleClick}
        className={cn('tw:h-7 tw:w-7 tw:p-0 tw:rounded-md', className)}
        {...props}
      >
        {copied ? <Check size={iconSize} aria-hidden="true" /> : <Copy size={iconSize} aria-hidden="true" />}
      </Button>
    );
  },
);

CopyButton.displayName = 'CopyButton';

export { CopyButton };
